import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  Download,
  User,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  MessageSquare,
  X,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  ShieldCheck,
  TrendingUp,
  Clock,
  ArrowRight,
  Kanban,
} from "lucide-react";
import apiClient from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import AppEmptyState from "@/components/ui/AppEmptyState";
import { TESTIDS } from "@/lib/testids";

import { useSearchParams } from "react-router-dom";
import { recruitmentApi } from "@/lib/recruitmentApi";

const STATUS_OPTIONS = [
  { value: "Nouveau", label: "Nouveau", color: "bg-blue-500/20 text-blue-400" },
  { value: "En cours", label: "En cours", color: "bg-yellow-500/20 text-yellow-400" },
  { value: "Entretien", label: "Entretien", color: "bg-purple-500/20 text-purple-400" },
  { value: "Accepté", label: "Accepté", color: "bg-green-500/20 text-green-400" },
  { value: "Qualifié", label: "Qualifié", color: "bg-teal-500/20 text-teal-400" },
  { value: "Refusé", label: "Refusé", color: "bg-red-500/20 text-red-400" },
];

const STAGE_LABELS = {
  qualification: { label: "Qualification RH", color: "bg-amber-500/20 text-amber-300" },
  validation_tech: { label: "Validation CTO", color: "bg-purple-500/20 text-purple-300" },
  validation_commercial: { label: "Validation CCO", color: "bg-blue-500/20 text-blue-300" },
  preparation_cv: { label: "Préparation CV", color: "bg-indigo-500/20 text-indigo-300" },
  presente: { label: "Présenté au client", color: "bg-cyan-500/20 text-cyan-300" },
  entretiens: { label: "Entretiens", color: "bg-emerald-500/20 text-emerald-300" },
  decision: { label: "Décision finale", color: "bg-teal-500/20 text-teal-300" },
};

const getStatusStyle = (status) => {
  const statusOption = STATUS_OPTIONS.find((s) => s.value === status);
  return statusOption?.color || "bg-slate-500/20 text-slate-400";
};

// Application Detail Modal
const ApplicationDetailModal = ({ application, onClose, onUpdate }) => {
  const [status, setStatus] = useState(application?.status || "Nouveau");
  const [notes, setNotes] = useState(application?.internal_notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Qualify states
  const [qualifyMode, setQualifyMode] = useState(false);
  const defaultFirstName = application.name?.split(" ")[0] || "";
  const defaultLastName = application.name?.split(" ").slice(1).join(" ") || "";
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [phone, setPhone] = useState(application?.phone || "");

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await apiClient.put(`/admin/candidatures/${application.id}`, {
        status,
        internal_notes: notes,
      });
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleQualify = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Create candidate
      const candidateRes = await apiClient.post("/recruitment/candidates", {
        first_name: firstName,
        last_name: lastName,
        email: application.email,
        phone,
        source: application.source || "Site web",
        cv_file_url: application.cv_url,
        status: "active"
      });
      const candidateId = candidateRes.data?._id || candidateRes.data?.id;

      // 2. Update status
      await apiClient.put(`/admin/candidatures/${application.id}`, {
        status: "Qualifié",
        internal_notes: notes,
      });
      
      import("react-hot-toast").then(({ toast }) => {
        toast.success(
          <div>
            Candidat ajouté au pipeline.<br/>
            {candidateId && (
              <Link to={`/admin/recrutement/candidats/${candidateId}`} className="underline font-medium">Voir le candidat</Link>
            )}
          </div>,
          { duration: 5000 }
        );
      });

      onUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la qualification");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg glass rounded-2xl p-6 my-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-bold text-white">
            {qualifyMode ? "Qualifier la candidature" : "Détail candidature"}
          </h2>
          <button
            onClick={() => qualifyMode ? setQualifyMode(false) : onClose()}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Candidate Info */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-lenvia-blue/20 flex items-center justify-center text-lenvia-blue font-bold text-lg">
              {application.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {application.name}
              </h3>
              {application.job_offer_title && (
                <p className="text-sm text-slate-400">
                  {application.job_offer_title}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <a
              href={`mailto:${application.email}`}
              className="flex items-center gap-2 text-slate-300 hover:text-lenvia-blue transition-colors"
            >
              <Mail className="w-4 h-4 text-slate-500" />
              {application.email}
            </a>
            {application.phone && (
              <a
                href={`tel:${application.phone}`}
                className="flex items-center gap-2 text-slate-300 hover:text-lenvia-blue transition-colors"
              >
                <Phone className="w-4 h-4 text-slate-500" />
                {application.phone}
              </a>
            )}
            {application.created_at && (
              <div className="flex items-center gap-2 text-slate-400">
                <Calendar className="w-4 h-4 text-slate-500" />
                Candidature reçue le{" "}
                {new Date(application.created_at).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            )}
          </div>

          {/* CV Download */}
          {application.cv_url && (
            <a
              href={application.cv_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-lenvia-blue/10 border border-lenvia-blue/20 text-lenvia-blue hover:bg-lenvia-blue/20 transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger le CV
              <ExternalLink className="w-3.5 h-3.5 ml-auto" />
            </a>
          )}
        </div>

        {qualifyMode ? (
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Prénom</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Nom</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                value={application.email}
                disabled
                className="bg-white/5 border-white/10 text-slate-400 opacity-70"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Téléphone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
        ) : (
          <>
            {/* Status */}
            <div className="space-y-2 mb-4">
              <Label className="text-slate-300">Statut de la candidature</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      className="text-white hover:bg-white/10"
                      disabled={opt.value === "Qualifié"}
                      title={opt.value === "Qualifié" ? "Utilisez 'Transformer en candidat' pour cette action" : undefined}
                    >
                      <span className={`px-2 py-0.5 rounded ${opt.color}`}>
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Internal Notes */}
            <div className="space-y-2 mb-6">
              <Label className="text-slate-300">Notes internes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ajouter des notes sur ce candidat..."
                className="min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {qualifyMode ? (
            <>
              <Button
                variant="outline"
                onClick={() => setQualifyMode(false)}
                className="flex-1 border-white/10 text-slate-300"
              >
                Retour
              </Button>
              <Button
                onClick={handleQualify}
                disabled={saving || !firstName || !lastName}
                className="flex-1 btn-primary"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Création...
                  </span>
                ) : (
                  "Créer le candidat"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setQualifyMode(true)}
                className="flex-1 border-lenvia-blue/20 text-lenvia-blue hover:bg-lenvia-blue/10"
              >
                Transformer en candidat
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 btn-primary"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Enregistrement...
                  </span>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const AdminCandidatures = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initialStage = searchParams.get("stage") || "all";
  const initialValidation = searchParams.get("validation") || "all";
  const initialSearch = searchParams.get("q") || searchParams.get("search") || "";
  const targetApplicationId = searchParams.get("application_id") || "";

  const [activeTab, setActiveTab] = useState(
    urlTab === "public" ? "public" : "workflow"
  );

  // Workflow V2 applications state
  const [v2Applications, setV2Applications] = useState([]);
  const [v2Loading, setV2Loading] = useState(true);
  const [v2Error, setV2Error] = useState(null);
  const [v2StageFilter, setV2StageFilter] = useState(initialStage);
  const [v2ValidationFilter, setV2ValidationFilter] = useState(initialValidation);

  // Public job offers applications state
  const [applications, setApplications] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState("all");
  const [offerFilter, setOfferFilter] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Keep state in sync with URL searchParams if URL changes
  useEffect(() => {
    if (urlTab === "public" && activeTab !== "public") {
      setActiveTab("public");
    } else if (urlTab === "workflow" && activeTab !== "workflow") {
      setActiveTab("workflow");
    }
  }, [urlTab, activeTab]);

  const updateUrlParams = useCallback((newTab, newStage, newVal, newSearch) => {
    const params = new URLSearchParams();
    if (newTab === "public") params.set("tab", "public");
    if (newStage && newStage !== "all") params.set("stage", newStage);
    if (newVal && newVal !== "all") params.set("validation", newVal);
    if (newSearch && newSearch.trim()) params.set("q", newSearch.trim());
    if (targetApplicationId) params.set("application_id", targetApplicationId);
    setSearchParams(params, { replace: true });
  }, [setSearchParams, targetApplicationId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    updateUrlParams(tab, v2StageFilter, v2ValidationFilter, search);
  };

  const handleStageChange = (stage) => {
    setV2StageFilter(stage);
    updateUrlParams(activeTab, stage, v2ValidationFilter, search);
  };

  const handleValidationChange = (val) => {
    setV2ValidationFilter(val);
    updateUrlParams(activeTab, v2StageFilter, val, search);
  };

  const handleSearchChange = (query) => {
    setSearch(query);
    updateUrlParams(activeTab, v2StageFilter, v2ValidationFilter, query);
  };

  const fetchV2Applications = useCallback(async () => {
    setV2Loading(true);
    setV2Error(null);
    try {
      const params = {};
      if (v2StageFilter !== "all") params.stage_v2 = v2StageFilter;
      if (v2ValidationFilter !== "all") params.validation = v2ValidationFilter;
      const res = await recruitmentApi.listApplications(params);
      setV2Applications(Array.isArray(res) ? res : res.items || []);
    } catch (err) {
      console.error("Erreur chargement candidatures v2:", err);
      setV2Error(err?.response?.data?.detail || "Impossible de charger les candidatures du workflow de mission.");
    } finally {
      setV2Loading(false);
    }
  }, [v2StageFilter, v2ValidationFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [appsRes, offersRes] = await Promise.all([
        apiClient.get("/admin/candidatures"),
        apiClient.get("/admin/offres"),
      ]);
      setApplications(appsRes.data || []);
      setOffers(offersRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchV2Applications();
  }, [fetchV2Applications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleUpdate = () => {
    setSelectedApplication(null);
    setSuccessMessage("Candidature mise à jour");
    fetchData();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Filter public applications
  const filteredApplications = applications.filter((app) => {
    const matchesSearch =
      app.name?.toLowerCase().includes(search.toLowerCase()) ||
      app.email?.toLowerCase().includes(search.toLowerCase()) ||
      app.job_offer_title?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || app.status === statusFilter;

    const matchesOffer =
      offerFilter === "all" || app.job_offer_id === offerFilter;

    return matchesSearch && matchesStatus && matchesOffer;
  });

  // Filter V2 applications
  const filteredV2Applications = v2Applications.filter((app) => {
    const cName = `${app.candidate?.first_name || ""} ${app.candidate?.last_name || ""}`.toLowerCase();
    const cEmail = (app.candidate?.email || "").toLowerCase();
    const nTitle = (app.need?.title || "").toLowerCase();
    const nClient = (app.need?.client || "").toLowerCase();
    const q = search.toLowerCase();
    return cName.includes(q) || cEmail.includes(q) || nTitle.includes(q) || nClient.includes(q);
  });

  return (
    <div className="space-y-6" data-testid={TESTIDS.recruitment.candidatures.page}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">
            Candidatures
          </h1>
          <p className="text-slate-400 mt-1">
            Gestion du pipeline de recrutement et des candidatures spontanées ou sur offre
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/recrutement/pipeline"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-md transition-all"
            data-testid="view-pipeline-button"
          >
            <Kanban className="w-4 h-4" />
            Voir le pipeline
          </Link>
          <Button
            onClick={() => {
              fetchV2Applications();
              fetchData();
            }}
            variant="outline"
            className="border-white/10 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex border-b border-white/10 gap-4">
        <button
          onClick={() => handleTabChange("workflow")}
          className={`pb-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "workflow"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
          data-testid="candidatures-tab-workflow"
        >
          <Briefcase className="w-4 h-4" />
          Besoins & Missions Clients (Suivi des candidatures)
          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300 font-semibold">
            {v2Applications.length}
          </span>
        </button>
        <button
          onClick={() => handleTabChange("public")}
          className={`pb-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "public"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
          data-testid="candidatures-tab-public"
        >
          <User className="w-4 h-4" />
          Offres Publiques (Candidatures du site)
          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-700 text-slate-300 font-semibold">
            {applications.length}
          </span>
        </button>
      </div>

      {/* Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2 text-green-400"
          >
            <CheckCircle2 className="w-4 h-4" />
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400"
          >
            <AlertCircle className="w-4 h-4" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* WORKFLOW V2 TAB */}
      {activeTab === "workflow" && (
        <div className="space-y-4">
          {/* Quick role productivity pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
            <span className="text-slate-500 font-medium whitespace-nowrap mr-1">Filtres rapides :</span>
            {[
              { label: "Tous", stage: "all", val: "all" },
              { label: "À qualifier (RH)", stage: "qualification", val: "all" },
              { label: "À valider (CTO)", stage: "all", val: "tech" },
              { label: "À valider (CCO)", stage: "all", val: "commercial" },
              { label: "Entretiens", stage: "entretiens", val: "all" },
              { label: "Décision finale", stage: "decision", val: "all" },
            ].map((pill) => {
              const isActive = v2StageFilter === pill.stage && v2ValidationFilter === pill.val;
              return (
                <button
                  key={pill.label}
                  type="button"
                  onClick={() => {
                    setV2StageFilter(pill.stage);
                    setV2ValidationFilter(pill.val);
                    updateUrlParams(activeTab, pill.stage, pill.val, search);
                  }}
                  className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors font-medium border ${
                    isActive
                      ? "bg-blue-600 border-blue-500 text-white shadow-xs"
                      : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Rechercher par candidat, besoin ou client..."
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
            <Select value={v2StageFilter} onValueChange={handleStageChange}>
              <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Étape du workflow" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all" className="text-white">Toutes les étapes</SelectItem>
                <SelectItem value="qualification" className="text-white">Qualification RH</SelectItem>
                <SelectItem value="validation_tech" className="text-white">Validation CTO</SelectItem>
                <SelectItem value="validation_commercial" className="text-white">Validation CCO</SelectItem>
                <SelectItem value="preparation_cv" className="text-white">Préparation CV</SelectItem>
                <SelectItem value="presente" className="text-white">Présenté au client</SelectItem>
                <SelectItem value="entretiens" className="text-white">Entretiens</SelectItem>
                <SelectItem value="decision" className="text-white">Décision finale</SelectItem>
              </SelectContent>
            </Select>
            <Select value={v2ValidationFilter} onValueChange={handleValidationChange}>
              <SelectTrigger className="w-full sm:w-44 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Validation" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all" className="text-white">Toutes validations</SelectItem>
                <SelectItem value="tech" className="text-white">Attente CTO</SelectItem>
                <SelectItem value="commercial" className="text-white">Attente CCO</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {v2Error ? (
            <div className="p-5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-sm space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
                <span className="font-semibold">Erreur de chargement du workflow de recrutement</span>
              </div>
              <p className="text-xs text-rose-300/80">{v2Error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchV2Applications}
                className="border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Réessayer
              </Button>
            </div>
          ) : v2Loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin" />
            </div>
          ) : filteredV2Applications.length === 0 ? (
            <AppEmptyState
              icon={Briefcase}
              title="Aucune candidature de mission trouvée"
              description="Les candidats positionnés sur des besoins clients apparaîtront ici."
            />
          ) : (
            <div className="space-y-3" data-testid="recruitment-v2-applications-table">
              {filteredV2Applications.map((app, index) => {
                const stageInfo = STAGE_LABELS[app.stage_v2] || {
                  label: app.stage_v2 || app.stage || "En cours",
                  color: "bg-slate-500/20 text-slate-300",
                };
                const candidateName = `${app.candidate?.first_name || ""} ${app.candidate?.last_name || "Candidat"}`;
                const isRejected = app.outcome === "ecarte" || app.stage === "rejected" || app.stage_v2 === "rejected";
                const isWaitingTech = app.stage_v2 === "validation_tech";
                const isWaitingComm = app.stage_v2 === "validation_commercial";

                return (
                  <motion.div
                    key={app.id || app._id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="glass rounded-xl p-4 sm:p-5 hover:bg-white/5 transition-colors border border-white/10 space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold">
                          {candidateName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/recrutement/candidats/${app.candidate_id}`}
                              className="text-white font-medium hover:text-blue-400 transition-colors flex items-center gap-1"
                            >
                              {candidateName}
                              <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                            </Link>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${stageInfo.color}`}>
                              {stageInfo.label}
                            </span>
                            {isWaitingTech && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/20 text-violet-300 border border-violet-500/30">
                                Action CTO requise
                              </span>
                            )}
                            {isWaitingComm && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                Action CCO requise
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {app.candidate?.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex-1 sm:px-4">
                        <p className="text-xs text-slate-400">Besoin client associé :</p>
                        <Link
                          to={`/admin/recrutement/pipeline?application_id=${app._id || app.id}`}
                          className="text-sm font-semibold text-white hover:text-blue-300 transition-colors"
                        >
                          {app.need?.title || "Parcours vivier"} • <span className="text-slate-400 font-normal">{app.need?.client || "Client"}</span>
                        </Link>
                      </div>

                      <div className="flex items-center gap-3">
                        {app.matching_score != null && (
                          <div className="text-right">
                            <span className="text-xs text-slate-400 block">Score IA</span>
                            <span className="text-sm font-black text-blue-400">
                              {Math.round(app.matching_score)}%
                            </span>
                          </div>
                        )}

                        <Link
                          to={`/admin/recrutement/pipeline?application_id=${app._id || app.id}`}
                          className="px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 text-xs font-semibold transition-colors flex items-center gap-1"
                        >
                          Voir le pipeline
                          <ArrowRight className="w-3 h-3 ml-0.5" />
                        </Link>
                      </div>
                    </div>

                    {/* Rejection or Validation status banners */}
                    {isRejected && (
                      <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
                        <span>
                          <strong>Dossier écarté</strong>
                          {app.rejection_source && ` (${app.rejection_source})`} : {app.rejection_reason || "Non retenu"}
                        </span>
                      </div>
                    )}

                    {/* Validations trace (CTO / CCO) */}
                    {(app.validations?.tech || app.validations?.commercial) && !isRejected && (
                      <div className="flex flex-wrap gap-2 text-[11px] pt-1 border-t border-white/5">
                        {app.validations?.tech && (
                          <span className="px-2 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20 flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Tech : {app.validations.tech.status === "approved" ? "Validé CTO" : "Refusé"} {app.validations.tech.comment ? `("${app.validations.tech.comment}")` : ""}
                          </span>
                        )}
                        {app.validations?.commercial && (
                          <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Commercial : {app.validations.commercial.status === "approved" ? "Validé CCO" : "Refusé"} {app.validations.commercial.comment ? `("${app.validations.commercial.comment}")` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* PUBLIC JOB OFFERS TAB */}
      {activeTab === "public" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un candidat..."
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all" className="text-white">Tous les statuts</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-white">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={offerFilter} onValueChange={setOfferFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Offre" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all" className="text-white">Toutes les offres</SelectItem>
                {offers.map((offer) => (
                  <SelectItem key={offer.id} value={offer.id} className="text-white">
                    {offer.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin" />
            </div>
          ) : filteredApplications.length === 0 ? (
            <AppEmptyState
              icon={User}
              title="Aucune candidature trouvée"
              description="Les nouvelles candidatures apparaîtront ici dès qu'une offre recevra des réponses."
            />
          ) : (
            <div className="space-y-3" data-testid={TESTIDS.recruitment.candidatures.table}>
              {filteredApplications.map((app, index) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => setSelectedApplication(app)}
                  className="glass rounded-xl p-4 sm:p-5 cursor-pointer hover:bg-white/5 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-lenvia-blue/20 flex items-center justify-center text-lenvia-blue font-bold">
                        {app.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-white font-medium truncate">
                          {app.name}
                        </h3>
                        <p className="text-sm text-slate-400 truncate">
                          {app.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex-1 flex flex-wrap items-center gap-2 sm:justify-center">
                      {app.job_offer_title && (
                        <span className="flex items-center gap-1 text-sm text-slate-400">
                          <Briefcase className="w-3.5 h-3.5" />
                          {app.job_offer_title}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusStyle(
                          app.status
                        )}`}
                      >
                        {app.status || "Nouveau"}
                      </span>
                      {app.created_at && (
                        <span className="text-xs text-slate-500 hidden sm:block">
                          {new Date(app.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                      {app.cv_url && (
                        <a
                          href={app.cv_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 text-slate-400 hover:text-lenvia-blue transition-colors"
                          title="Télécharger le CV"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {app.internal_notes && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-sm text-slate-500 flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{app.internal_notes}</span>
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedApplication && (
          <ApplicationDetailModal
            application={selectedApplication}
            onClose={() => setSelectedApplication(null)}
            onUpdate={handleUpdate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCandidatures;
